/**
 * Public booking page (partnership pages without packs): the campaign's
 * website pages with the sellable spots framed, a day-by-day overview, and
 * per placement its dated slots to tick. The selection lives in the parent
 * (CampaignPartnership), which shows the total and the Sign up button.
 */
import { useMemo, useRef, useState } from 'react';
import SectionStack from '../placements/SectionStack';
import { BookingPlacement, BookingSlot, PublicBooking } from '../../lib/api/partnerships';
import { parseDate } from '../../lib/dates';
import { Lang, StrKey, T, fmtDay, fmtNum, fmtWeekday } from '../../lib/bookingI18n';

const LT = {
  paper: '#fbfbfa', bone: '#edece5', ink: '#2b4138', ink700: '#3d5448', ink500: '#6d8276', ink300: '#a8b6ad',
  yellow: '#ffbd09', yellow50: '#fff6d9', cta: '#469e7a', border: 'rgba(43,65,56,0.12)', rCard: 14,
};
const slotDates = (lang: Lang, s: BookingSlot) =>
  (s.end && s.end !== s.start ? `${fmtDay(lang, s.start)} – ${fmtDay(lang, s.end)}` : fmtDay(lang, s.start));
/** Days a slot runs (1 for a single day). Its numbers are stored per day;
 *  a spot that runs several days shows the total for its period. */
const slotDays = (s: BookingSlot) =>
  !s.end || s.end === s.start
    ? 1
    : Math.round((parseDate(s.end.slice(0, 10)).getTime() - parseDate(s.start.slice(0, 10)).getTime()) / 86400000) + 1;
/** Distinct campaign days a placement's slots cover — not the slot count:
 *  one spot running the whole week covers 8 days, not 1. */
const coveredDays = (slots: BookingSlot[]) => {
  const seen = new Set<number>();
  slots.forEach((s) => {
    const first = Math.round(parseDate(s.start.slice(0, 10)).getTime() / 86400000);
    for (let i = 0; i < slotDays(s); i++) seen.add(first + i);
  });
  return seen.size;
};
export const priceOf = (s: BookingSlot) => (s.price == null || s.price === '' ? null : Number(s.price));
const eur = (n: number) => `€${n.toLocaleString('en', { maximumFractionDigits: 0 })}`;
/** Placement texts: **…** shows in bold (Doreen, 2026-10-27). */
const withBold = (text: string) =>
  text.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));
const PART = (t: string) => t.replace('Instagram ', '').toLowerCase();
const PAGE_TITLE: Record<string, StrKey> = { home: 'pageHome', collection: 'pageCollection', product: 'pageProduct' };

const STYLES = `
.bk-days-note { margin: 0 0 14px; padding: 12px 16px; border-left: 3px solid ${LT.yellow}; background: ${LT.yellow50}; border-radius: 0 10px 10px 0; font-size: 15px; line-height: 1.5; color: ${LT.ink700}; max-width: 72ch; }
.bk-days { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(76px, 1fr); gap: 10px; overflow-x: auto; padding-bottom: 4px; }
.bk-day { background: ${LT.paper}; border: 1px solid ${LT.border}; border-radius: 10px; padding: 10px 10px 8px; text-align: left; font: inherit; cursor: pointer; }
.bk-day:hover:not(:disabled) { border-color: ${LT.ink300}; }
.bk-day:disabled { cursor: default; opacity: 0.5; }
.bk-day[aria-pressed="true"] { background: ${LT.ink}; border-color: ${LT.ink}; }
.bk-day[aria-pressed="true"] .d, .bk-day[aria-pressed="true"] .n, .bk-day[aria-pressed="true"] .l { color: ${LT.paper}; }
.bk-tip { display: flex; gap: 6px; align-items: baseline; font-size: 13.5px; line-height: 1.35; color: ${LT.ink500}; margin-top: 10px; }
.bk-tip b { color: ${LT.ink}; }
.bk-dayhint { font-size: 14.5px; }
.bk-dayhint button { border: 0; background: none; padding: 0; font: inherit; color: ${LT.cta}; text-decoration: underline; cursor: pointer; }
.bk-day .d { font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; color: ${LT.ink500}; }
.bk-day .n { font-size: 22px; font-weight: 300; color: ${LT.ink}; margin-top: 2px; }
.bk-day .bar { height: 4px; border-radius: 999px; background: ${LT.bone}; margin-top: 8px; overflow: hidden; }
.bk-day .bar span { display: block; height: 100%; background: ${LT.cta}; }
.bk-day .l { font-size: 12px; color: ${LT.ink500}; margin-top: 4px; }
.bk-day.full { background: ${LT.bone}; }
.bk-day.full .n, .bk-day.full .d, .bk-day.full .l { color: ${LT.ink300}; }
.bk-grid { display: grid; grid-template-columns: minmax(0, 1.45fr) minmax(320px, 1fr); gap: 36px; align-items: start; margin-top: 44px; }
@media (max-width: 900px) { .bk-grid { grid-template-columns: 1fr; } }
.bk-step { display: flex; align-items: center; gap: 10px; font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; font-weight: 700; color: ${LT.ink}; margin: 0 0 12px; }
.bk-step .no { width: 24px; height: 24px; border-radius: 50%; background: ${LT.yellow}; color: ${LT.ink}; display: grid; place-items: center; font-size: 12px; letter-spacing: 0; }
.bk-step .sub { text-transform: none; letter-spacing: 0; font-weight: 400; color: ${LT.ink500}; font-size: 13px; }
.bk-tabs { display: flex; gap: 8px; margin-bottom: 28px; flex-wrap: wrap; padding: 6px; background: ${LT.paper}; border: 1px solid ${LT.border}; border-radius: 16px; }
.bk-tabs button { flex: 1 1 auto; font-size: 15.5px !important; border: 1px solid transparent; background: transparent; color: ${LT.ink700}; border-radius: 12px; padding: 12px 18px; font: inherit; font-size: 15px; font-weight: 500; cursor: pointer; white-space: nowrap; }
.bk-tabs button:hover { background: ${LT.bone}; }
.bk-tabs button[aria-pressed="true"] { background: ${LT.ink}; color: ${LT.paper}; box-shadow: 0 2px 8px rgba(43,65,56,0.18); }
.bk-sep { display: flex; align-items: center; gap: 12px; margin: 22px 0 14px; }
.bk-sep::before, .bk-sep::after { content: ""; flex: 1; height: 1px; background: ${LT.ink300}; }
.bk-sep span { font-size: 18px; color: ${LT.ink500}; }
.bk-map { max-height: 1200px; overflow: auto; border-radius: 12px; }
@media (max-width: 900px) { .bk-map { max-height: 760px; } }
.bk-shots { display: grid; grid-template-columns: 1fr; gap: 14px; align-items: start; }
.bk-shots img { width: 100%; border-radius: 10px; border: 1px solid ${LT.border}; background: #fff; display: block; }
.bk-none { padding: 40px 20px; text-align: center; font-size: 14px; color: ${LT.ink500}; background: ${LT.paper}; border: 1px dashed ${LT.border}; border-radius: 12px; }
.bk-list { display: grid; gap: 10px; }
.bk-item { text-align: left; background: ${LT.paper}; border: 1px solid ${LT.border}; border-radius: 12px; padding: 16px 18px; cursor: pointer; font: inherit; color: ${LT.ink}; display: grid; grid-template-columns: 1fr auto; gap: 4px 10px; }
.bk-item:hover { border-color: ${LT.ink300}; }
.bk-item[aria-pressed="true"] { border-color: ${LT.ink}; box-shadow: 0 0 0 1px ${LT.ink}; }
.bk-item .nm { font-size: 16.5px; font-weight: 500; }
.bk-item .meta { font-size: 13.5px; color: ${LT.ink500}; grid-column: 1 / -1; }
.bk-tabs .pk, .bk-item .pk { display: inline-block; margin-left: 8px; padding: 1px 8px; border-radius: 999px; background: ${LT.cta}; color: #fff; font-size: 11px; font-weight: 600; vertical-align: 1px; }
.bk-item .cnt { font-size: 13px; font-weight: 600; color: ${LT.cta}; white-space: nowrap; }
.bk-item .cnt.full { color: ${LT.ink300}; }
.bk-panel { background: ${LT.paper}; border: 1px solid ${LT.border}; border-top: 4px solid ${LT.yellow}; border-radius: ${LT.rCard}px; padding: 26px; display: grid; gap: 16px; box-shadow: 0 6px 20px rgba(43,65,56,0.08); }
.bk-panel h3 { margin: 0; font-size: 26px; font-weight: 400; letter-spacing: -0.01em; color: ${LT.ink}; }
.bk-panel .desc { font-size: 15.5px; line-height: 1.55; color: ${LT.ink700}; margin: 0; }
/* The brand font only has a light (300) cut: plain <strong> becomes 400 and
   looks the same — ask for 700 so the browser draws it bold. */
.bk-panel .desc strong, .bk-panel .req strong { font-weight: 700; font-synthesis: weight; color: ${LT.ink}; }
.bk-panel .req { font-size: 14.5px; line-height: 1.5; color: ${LT.ink700}; background: ${LT.yellow50}; border-radius: 10px; padding: 10px 12px; }
.bk-panel .nums { font-size: 14px; color: ${LT.ink500}; }
.bk-slots { display: grid; gap: 8px; }
.bk-slot { display: grid; grid-template-columns: 22px 1fr auto; gap: 12px; align-items: center; padding: 14px 16px; border: 1px solid ${LT.border}; border-radius: 10px; cursor: pointer; font-size: 15px; color: ${LT.ink}; background: #fff; }
.bk-slot.on { border-color: ${LT.cta}; background: #eef7f2; }
.bk-slot.hl { border-color: ${LT.yellow}; box-shadow: inset 3px 0 0 ${LT.yellow}; }
.bk-slot .star { display: inline-block; margin-left: 8px; font-size: 12px; font-weight: 600; color: #a87800; }
.bk-slot .stats { display: block; font-size: 12.5px; color: ${LT.ink500}; margin-top: 2px; }
.bk-slot.full { cursor: not-allowed; color: ${LT.ink300}; background: ${LT.bone}; }
.bk-slot .sub { font-size: 13px; color: ${LT.ink500}; }
.bk-slot .pr { font-size: 15px; font-weight: 600; white-space: nowrap; text-align: right; }
.bk-slot .pr .left { display: block; font-size: 12.5px; font-weight: 500; color: ${LT.cta}; }
.bk-slot.full .pr .left { color: ${LT.ink300}; }
`;

export default function BookingSection({
  booking, start, end, selected, onToggle, locked, daysNote, lang, t,
}: {
  lang: Lang;
  t: T;
  booking: PublicBooking;
  start: string;
  end: string;
  selected: Set<number>;
  onToggle: (slotId: number) => void;
  /** Already signed up (or opt-ins closed): show, don't let them tick. */
  locked: boolean;
  /** Page's line next to the day picker (already in the brand's language). */
  daysNote?: string;
}) {
  const { placements: raw, pages: rawPages } = booking;
  // The brand's language: placement texts from their translations (else English).
  const placements = useMemo(() => raw.map((p) => {
    const tr = lang !== 'en' ? p.translations?.[lang] : undefined;
    return tr ? { ...p, name: tr.name || p.name, description: tr.description || p.description, requirements: tr.requirements || p.requirements } : p;
  }), [raw, lang]);
  const pages = useMemo(() => {
    const names = new Map(placements.map((p) => [p.key, p.name]));
    return rawPages.map((pg) => ({
      ...pg,
      title: PAGE_TITLE[pg.key] ? t(PAGE_TITLE[pg.key]) : pg.title,
      sections: pg.sections.map((sec) => ({ ...sec, frames: sec.frames.map((f) => ({ ...f, rate_label: names.get(f.rate_key ?? '') ?? f.rate_label })) })),
    }));
  }, [rawPages, placements, t]);
  const onMap = useMemo(
    () => new Set(pages.flatMap((p) => p.sections.flatMap((s) => s.frames.map((f) => f.rate_key ?? '')))),
    [pages],
  );
  // Left side: one tab per website page, then one per placement that isn't
  // on the website (its mockup pictures). Tab key = "page:<key>" / "pl:<key>".
  // Placements with no slot left: greyed out on the website preview.
  const fullKeys = useMemo(
    () => new Set(placements.filter((p) => p.slots.length && p.slots.every((x) => x.left === 0 && !x.mine)).map((p) => p.key)),
    [placements],
  );
  const offsite = placements.filter((p) => !onMap.has(p.key));
  const [tab, setTab] = useState<string>(pages[0] ? `page:${pages[0].key}` : offsite[0] ? `pl:${offsite[0].key}` : '');
  const [active, setActive] = useState<string>(placements[0]?.key ?? '');
  // Optional day filter: click a day to see only what can be booked that day.
  const [day, setDay] = useState<string | null>(null);
  const onDay = (s: BookingSlot) => !day || (s.start <= day && (s.end || s.start) >= day);
  const hasDay = (p: BookingPlacement) => p.slots.some(onDay);
  const current = placements.find((p) => p.key === active) ?? placements[0];
  const page = tab.startsWith('page:') ? pages.find((p) => `page:${p.key}` === tab) : undefined;
  const offTab = tab.startsWith('pl:') ? offsite.find((p) => `pl:${p.key}` === tab) : undefined;
  // What sits on each website page (rate keys), for the filtered list.
  const keysOn = (pk: string) =>
    new Set(pages.find((p) => p.key === pk)?.sections.flatMap((s) => s.frames.map((f) => f.rate_key ?? '')) ?? []);
  const pickedIn = (pl: BookingPlacement) => pl.slots.filter((s) => selected.has(s.id)).length;
  const pickedOnTab = (t: string) => {
    if (t.startsWith('pl:')) {
      const pl = placements.find((p) => `pl:${p.key}` === t);
      return pl ? pickedIn(pl) : 0;
    }
    const keys = keysOn(t.slice(5));
    return placements.filter((p) => keys.has(p.key)).reduce((n, p) => n + pickedIn(p), 0);
  };
  // The list on the right follows the tab on the left.
  const visible = (page
    ? placements.filter((p) => keysOn(page.key).has(p.key))
    : offTab ? [offTab] : placements).filter(hasDay);
  const shown = visible.find((p) => p.key === active) ?? visible[0] ?? current;
  // Picking a placement on the right shows where it is on the left.
  // `scroll`: picked from the list on the right → bring its frame into view
  // in the preview on the left (and the preview onto the screen).
  const mapRef = useRef<HTMLDivElement>(null);
  const pick = (key: string, scroll = false) => {
    setActive(key);
    const pg = pages.find((p) => p.sections.some((s) => s.frames.some((f) => f.rate_key === key)));
    if (pg) setTab(`page:${pg.key}`);
    else if (offsite.some((p) => p.key === key)) setTab(`pl:${key}`);
    if (scroll) requestAnimationFrame(() => requestAnimationFrame(() => scrollToFrame(key)));
  };
  const scrollToFrame = (key: string) => {
    const box = mapRef.current;
    if (!box) return;
    const frame = box.querySelector<HTMLElement>(`[data-rate="${CSS.escape(key)}"]`);
    if (frame) {
      const top = frame.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - 40;
      box.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
    const r = box.getBoundingClientRect();
    if (r.top < 0 || r.top > window.innerHeight * 0.6) {
      window.scrollBy({ top: r.top - 90, behavior: 'smooth' });
    }
  };

  // Campaign days with how many slots are still free that day.
  const days = useMemo(() => {
    const out: { iso: string; free: number; total: number }[] = [];
    const a = parseDate(start);
    const b = parseDate(end);
    for (let d = new Date(a); d <= b && out.length < 62; d.setDate(d.getDate() + 1)) {
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      let free = 0;
      let total = 0;
      for (const p of placements) for (const s of p.slots) {
        if (s.start <= iso && (s.end || s.start) >= iso) { free += s.left; total += s.spots; }
      }
      out.push({ iso, free, total });
    }
    return out;
  }, [placements, start, end]);

  const fromPrice = (p: BookingPlacement) => {
    const ps = p.slots.map(priceOf).filter((n): n is number => n != null);
    return ps.length ? Math.min(...ps) : null;
  };

  if (!placements.length) {
    return (
      <p style={{ fontSize: 15, color: LT.ink700 }}>
        {t('preparing')}
      </p>
    );
  }

  return (
    <div>
      <style>{STYLES}</style>
      {daysNote && <p className="bk-days-note">{withBold(daysNote)}</p>}
      <div className="bk-days" aria-label="Free slots per day">
        {days.map((d) => (
          <button
            key={d.iso}
            type="button"
            className={`bk-day${d.total && !d.free ? ' full' : ''}`}
            aria-pressed={day === d.iso}
            disabled={!d.total}
            title={d.total ? `${d.free} / ${d.total}` : ''}
            onClick={() => setDay(day === d.iso ? null : d.iso)}
          >
            <div className="d">{fmtWeekday(lang, d.iso)}</div>
            <div className="n">{parseDate(d.iso).getDate()}</div>
            <div className="bar"><span style={{ width: d.total ? `${(d.free / d.total) * 100}%` : '0%' }} /></div>
            <div className="l">{!d.total ? '—' : d.free ? t('free', { n: d.free }) : t('fullyBooked')}</div>
          </button>
        ))}
      </div>
      <div className="bk-tip bk-dayhint">
        <span aria-hidden>💡</span>
        <span>
        {day
          ? <>{t('showingOnly')} <strong>{fmtDay(lang, day)}</strong> · <button type="button" onClick={() => setDay(null)}>{t('showAllDays')}</button></>
          : <><b>{t('tip')}</b> {t('tipDay')}</>}
        </span>
      </div>

      <div className="bk-grid">
        <div>
          {(pages.length > 0 || offsite.length > 0) && (
            <>
              <div className="bk-step"><span className="no">1</span>{t('step1')}</div>
              <div className="bk-tabs">
                {pages.map((p) => {
                  const t = `page:${p.key}`;
                  const n = pickedOnTab(t);
                  return (
                    <button
                      key={p.key}
                      type="button"
                      aria-pressed={tab === t}
                      onClick={() => {
                        setTab(t);
                        const first = placements.find((pl) => keysOn(p.key).has(pl.key));
                        if (first && !keysOn(p.key).has(active)) setActive(first.key);
                      }}
                    >
                      {p.title}{n ? <span className="pk" title={`${n} picked`}>{n}</span> : null}
                    </button>
                  );
                })}
                {offsite.map((p) => {
                  const n = pickedIn(p);
                  return (
                    <button key={p.key} type="button" aria-pressed={tab === `pl:${p.key}`} onClick={() => pick(p.key)}>
                      {p.name}{n ? <span className="pk" title={`${n} picked`}>{n}</span> : null}
                    </button>
                  );
                })}
              </div>
              {page && (
                <>
                  <div className="bk-map" ref={mapRef}>
                    <SectionStack
                      sections={page.sections}
                      highlight={shown ? new Set([shown.key]) : new Set()}
                      bookingLook
                      fullKeys={fullKeys}
                      fullLabel={t('fullyBooked')}
                      oneSlotLabel={t('oneSlot')}
                      onSelectFrame={(f) => f.rate_key && pick(f.rate_key)}
                    />
                  </div>
                  <div className="bk-tip"><span aria-hidden>💡</span><span><b>{t('tip')}</b> {t('tipFrame')}</span></div>
                </>
              )}
              {offTab && (
                <div className="bk-map bk-shots">
                  {offTab.example_images.length ? (
                    offTab.example_images.map((src, i) => <img key={i} src={src} alt={`${offTab.name} — example ${i + 1}`} />)
                  ) : (
                    <div className="bk-none">{t('exampleSoon')}</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'grid', gap: 14 }}>
          {visible.length > 1 && (
          <div className="bk-step"><span className="no">2</span>{t('step2')}</div>
          )}
          {visible.length > 1 && (
          <div className="bk-list">
            {visible.map((p) => {
              const left = p.slots.reduce((n, s) => n + s.left, 0);
              const from = fromPrice(p);
              const picked = pickedIn(p);
              return (
                <button key={p.key} type="button" className="bk-item" aria-pressed={p.key === shown?.key} onClick={() => pick(p.key, true)}>
                  <span className="nm">{p.name}{picked ? <span className="pk">{picked}</span> : null}</span>
                  <span className={`cnt${left ? '' : ' full'}`}>{left ? t('left', { n: left }) : t('fullyBooked')}</span>
                  <span className="meta">
                    {(() => {
                      const n = coveredDays(p.slots);
                      if (p.slots.length === 1 && n > 1) return t('allDays', { n });
                      // Several one-day spots: you book a day at a time.
                      if (p.slots.length > 1 && p.slots.every((s) => slotDays(s) === 1)) return t('perDay');
                      return n === 1 ? t('day1') : t('days', { n });
                    })()}
                    {' · '}{from != null ? t('from', { p: eur(from) }) : t('priceOnRequest')}
                    {!onMap.has(p.key) ? ` · ${t('notOnWebsite')}` : ''}
                  </span>
                </button>
              );
            })}
          </div>
          )}

          {day && visible.length === 0 && (
            <div className="bk-none">{t('nothingThatDay', { day: fmtDay(lang, day) })}</div>
          )}
          {shown && visible.length > 0 && (
            <>
            {visible.length > 1 && <div className="bk-sep" aria-hidden><span>↓</span></div>}
            <div className="bk-step">
              <span className="no">{visible.length > 1 ? 3 : 2}</span>{t('step3')}
            </div>
            <div className="bk-panel">
              <h3>{shown.name}</h3>
              <div className="bk-tip" style={{ marginTop: 0 }}><span aria-hidden>💡</span><span>{t('tipTick')}</span></div>
              {shown.description && <p className="desc">{withBold(shown.description)}</p>}
              {shown.requirements && <div className="req"><strong>{t('whatWeNeed')}</strong> {withBold(shown.requirements)}</div>}
              {shown.max_per_brand ? (
                <div className="bk-tip" style={{ marginTop: 0 }}>
                  <span aria-hidden>ℹ️</span>
                  <span>{t(shown.max_per_brand === 1 ? 'maxOne' : 'maxN', { n: shown.max_per_brand })}</span>
                </div>
              ) : null}
              {(() => {
                // Only the spots that show numbers decide the wording: per day,
                // for the whole period, or both when a card mixes the two.
                const withStats = shown.slots.filter((x) => x.stats?.some((n) => n != null));
                if (!withStats.length) return null;
                const multi = withStats.filter((x) => slotDays(x) > 1).length;
                const key: StrKey = multi === 0 ? 'statsPerDay' : multi === withStats.length ? 'statsPeriod' : 'statsMixed';
                return <div className="bk-tip" style={{ marginTop: 0 }}><span aria-hidden>📈</span><span>{t(key)}</span></div>;
              })()}
              <div className="bk-slots">
                {shown.slots.filter(onDay).map((s) => {
                  const on = selected.has(s.id) || s.mine;
                  const full = s.left === 0 && !on;
                  // At the maximum per brand: the other days can't be ticked.
                  const capped = !on && !!shown.max_per_brand
                    && shown.slots.filter((x) => selected.has(x.id) || x.mine).length >= shown.max_per_brand;
                  const p = priceOf(s);
                  return (
                    <label key={s.id} className={`bk-slot${on ? ' on' : ''}${full || capped ? ' full' : ''}${s.highlighted && !full ? ' hl' : ''}`} title={capped ? t(shown.max_per_brand === 1 ? 'maxOne' : 'maxN', { n: shown.max_per_brand ?? 0 }) : undefined}>
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={full || capped || locked || s.mine}
                        onChange={() => onToggle(s.id)}
                        style={{ accentColor: LT.cta }}
                      />
                      <span>
                        {slotDates(lang, s)}
                        {s.with.length > 0 && <span className="sub"> · {t('with')} {[...new Set(s.with)].map(PART).join(' + ')}</span>}
                        {s.mine && <span className="sub"> · {t('bookedByYou')}</span>}
                        {s.highlighted && !full && <span className="star">{t('standOut')}</span>}
                        {(() => {
                          const labels = shown.stat_labels ?? ['page_views', 'sessions'];
                          const days = slotDays(s);
                          const bits = (s.stats ?? []).map((n, i) => (n != null ? t(`stat_${labels[i]}` as StrKey, { n: fmtNum(lang, n * days) }) : '')).filter(Boolean);
                          if (bits.length && days > 1) bits.push(t('statsOverDays', { n: days }));
                          return bits.length ? <span className="stats">{bits.join(' · ')}</span> : null;
                        })()}
                      </span>
                      <span className="pr">
                        {p != null ? eur(p) : t('onRequest')}
                        <span className="left">{full ? t('fullyBooked') : t('leftOf', { n: s.left, m: s.spots })}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
